import { ResultBase } from '@intranet/core/types';
import {
    ActionEdit,
    JobAction,
    StatusOption,
} from '@intranet/modules/actions/action.types';
import { Client } from '@intranet/modules/clients/client.types';
import { User } from '@intranet/modules/users/user.types';

export class JobType {
    public jobTypeId: number;
    public description: string;
    public active: boolean;
    public oldJobTypeId: number;
    public count: number | null;
}

export enum JobStatus {
    Live = 20,
    Unknown = 40,
    Completed = 50,
    Archived = 60,
}

/** Lightweight job object returned by the /api/job/jobsearch endpoint. */
export interface ShortJob {
    jobId: number;
    jobNumber: number;
    jobTitle: string;
    clientName: string;
    isBillable: boolean;
    isArchived: boolean;
}

interface IIndexedNames {
    [index: number]: string;
}

export const JobStatusName: IIndexedNames = {
    10: 'Not Live',
    20: 'Live',
    30: 'Live - On Hold',
    40: 'Unknown',
    50: 'Completed',
    60: 'Archived',
};

export class Job {
    public jobId: number;
    public jobTypeId: number;
    public jobType: JobType;
    public jobNumber: number;
    public clientId: number;
    public client: Client;
    public jobTitle: string;
    public jobOwnerId: string;
    public jobOwner: User;
    public jobStatus: JobStatus;
    public active: boolean;
    public archive: boolean;
    public deliveryDate: string;
    public dateCreated: string;
    public dateArchived: string | null;
    public firstTimeRecordedDate: string | null;
    public jobNoOR: number;
    public jobActions: JobAction[];
    public liveActionsDue: boolean;
    public liveActionsOverdue: boolean;
    public hasScheduledActions: boolean;
    public canArchive: boolean;
    public togglProjectId: string | null;
    public isBillable: boolean;
    public rocks: boolean;
    public marginValueStatus: JobValue;
}

export class JobDetailWithActionStatuses {
    constructor(detail: JobDetailWithActionStatuses) {
        this.statusOptions = detail.statusOptions;
        this.job = Object.assign(new Job(), detail.job);
        this.mostRecentActionUpdate = detail.mostRecentActionUpdate;
        this.isUnchangedSinceLast = detail.isUnchangedSinceLast;
        this.actionEdits = detail.actionEdits;
        this.jobTypes = detail.jobTypes;
        this.allowCompleted = detail.allowCompleted;
        this.users = detail.users;
        this.clientJobs = detail.clientJobs;
        this.jiraLinks = detail.jiraLinks;
    }
    public job: Job;
    public statusOptions: StatusOption[];
    public mostRecentActionUpdate: string; // UTC date really
    public isUnchangedSinceLast: boolean;
    public actionEdits: ActionEdit[];
    public jobTypes: JobType[];
    public allowCompleted: boolean;
    public users: User[];
    public clientJobs: Job[];
    public jiraLinks: JobJiraLink[];
    public linkedJobsValue: JobValue;
}

export class JobEditDetails {
    public job: Job;
    public jobTypes: JobType[];
    public users: User[];
    public allowCompleted: boolean;
}

export class JobUpdateResult {
    public success: boolean;
    public error: string;
    public job: Job;
}

export class JobListResult extends ResultBase {
    public jobs: Job[];
}

export class FilteredJobListResult extends JobListResult {
    public jobTypes: JobType[];
}

export class SetPrimaryQuoteForJobResult extends ResultBase {}

export interface JobSearchQuery {
    clientId?: number;
    clients?: number[];
    userId?: string;
    searchTerm?: string;
    page: number;
    isPaginating?: boolean;
    sortBy?: string;
    sortDir?: 'asc' | 'desc';
    archived?: boolean;
    status?: JobStatus;
    resultsPerPage?: number;
    active?: 'All' | 'Active' | 'Inactive';
}

export class StatusDetail {
    constructor(public status: JobStatus, public description: string) {}
    disabled: boolean = false;
}

export class UserOption {
    public name: string;
    public value: User | null;
    public disabled: boolean;
}

export enum ActionsToShow {
    Live,
    Completed,
    Archived,
}

export interface JobMarginUpdate {
    jobId: number;
    estimates: [
        {
            financeVersionId: number;
            margins: [
                {
                    month: Date;
                    status: number;
                    value: number;
                    included: boolean;
                    description: string;
                }
            ];
        }
    ],
    additional: [
        {
            month: Date;
            status: number;
            value: number;
            included: boolean;
            description: string;
        }
    ]
}

export interface UpdateJiraLinksForJob {
    jobId: number;
    links: JobJiraLink[];
}

export interface JobJiraLink {
    jiraLinkId: number;
    jobId: number;
    url: string;
    title: string;
    issue: string;
    board: string;
    isIssue: boolean;
}

export interface JiraLinksForJob {
    success: boolean;
    error: string;
    links: JobJiraLink[];
}

export interface JiraNameResult {
    jiraName: string;
    issue: string;
    board: string;
    isIssue: boolean;
    success: boolean;
    error: string;
}

export interface UserHours {
    user: string;
    hours: number;
}

export interface JobValue {
    jobId: number;
    hours: number;
    userHours: UserHours[];
    value: number;
    margin: number;
    ragStatus: string;
    isLinked?: boolean;
    linkedJobs?: { jobNumber: string; jobTitle: string; isParent?: boolean }[];
}

export interface JobValueDataForYear {
    jobId: number,
    year: number,
    isParent: boolean,
    users: string[],
    total: JobValue,
    previous: JobValue,
    months: JobValue[],
    jobNumber: number,
    jobTitle: string,
    notes?: string;
}

export interface JobReportValues {
    jobId: number;
    jobTitle: string;
    jobNumber: number;
    jobType: string;
    jobOwner: string;
    jobOwnerId?: string;
    clientName: string;
    clientId: number;
    isLinked: boolean;
    parentJobId: number | null;
    hours: number;
    value: number;
    margin: number;
    netmargin: number;
    linkedJobs: JobReportValues[];
    isTotalRow?: boolean;
    originalParent?: JobReportValues;
    netMargin?: number;
    mph?: number;
    percentBillable?: number;
    isBillable?: boolean;
    teamName?: string;
    clientGroupId?: number | null;
    clientGroupName?: string | null;
}

export interface JobTimeValuesQuery {
    clientId?: number;
    clientIds?: number[];
    searchTerm?: string;
    jobType?: string;
    jobOwnerId?: string;
    groupId?: number | null;
    groupLinked?: boolean;
    includeArchived: 'Archived' | 'Live' | 'All';
    includeBillable: 'Billable' | 'NonBillable' | 'All';
    reportType: 'Live' | 'Archived' | 'Snapshot';
    sortBy?: 'jobtitle' | 'jobnumber' | 'jobtype' | 'jobowner' | 'client' | 'hours' | 'value' | 'margin' | 'netmargin' | 'mph' | 'datecreated' | 'pcbilled';
    sortDir?: 'asc' | 'desc';
    page?: number;
    resultsPerPage?: number;
    dateFrom?: Date | null;
    dateTo?: Date | null;
}

export interface BillableHours {
    totalBillableHours: number;
    percentageOfTotalBillable: number;
    totalNonBillableHours: number;
    percentageOfTotalNonBillable: number;
    totalHours: number;
}

export interface BillableHoursForUser {
    userId: string;
    userName: string;
    shortName: string;
    teamName: string;
    totalBillableHours: number;
    percentageOfTotalBillable: number;
    totalNonBillableHours: number;
    percentageOfTotalNonBillable: number;
    totalHours: number;
    clients?: BillableHoursForClientSummary[];
}

export interface BillableHoursForUserResult {
    success: boolean;
    error: string;
    billableHours: BillableHoursForUser[];
    totals: BillableHours;
}

export interface BillableHoursForJobType {
    jobTypeId: number;
    jobTypeName: string;
    totalBillableHours: number;
    percentageOfTotalBillable: number;
    totalNonBillableHours: number;
    percentageOfTotalNonBillable: number;
    totalHours: number;
    clients?: BillableHoursForClientSummary[];
}

// Leaner Client/Job pair for the Users and JobType reports' two-level drill-down
// (Clients -> Jobs, no further per-job Users breakdown - mirrors the backend's
// BillableHoursForClientSummary/BillableHoursForJobSummary).
export interface BillableHoursForJobSummary {
    jobId: number;
    jobNumber: number;
    jobTitle: string;
    isBillable: boolean;
    totalBillableHours: number;
    totalNonBillableHours: number;
    totalHours: number;
}

export interface BillableHoursForClientSummary {
    clientId: number | null;
    clientName: string;
    totalBillableHours: number;
    percentageOfTotalBillable: number;
    totalNonBillableHours: number;
    percentageOfTotalNonBillable: number;
    totalHours: number;
    jobs?: BillableHoursForJobSummary[];
}

export interface BillableHoursForJobTypeResult {
    success: boolean;
    error: string;
    billableHours: BillableHoursForJobType[];
    totals: BillableHours;
}

export interface BillableHoursForClientJob {
    jobId: number;
    jobNumber: number;
    jobTitle: string;
    totalBillableHours: number;
    totalNonBillableHours: number;
    totalHours: number;
    isBillable: boolean;
    // Sent by the backend (BillableHoursForJob.Users) but previously missing from this
    // type even though report-billable.component.html already reads job.users.
    users?: BillableHoursForUser[];
}

export interface BillableHoursForClient {
    clientId: number | null;
    clientGroupId: number | null;
    clientName: string;
    totalBillableHours: number;
    percentageOfTotalBillable: number;
    totalNonBillableHours: number;
    percentageOfTotalNonBillable: number;
    totalHours: number;
    jobs?: BillableHoursForClientJob[];
}

export interface BillableHoursForClientResult {
    success: boolean;
    error: string;
    billableHours: BillableHoursForClient[];
    totals: BillableHours;
}

export interface JobTypeToEditResult {
    success: boolean;
    error: string;
    jobType: JobType;
    isCreate: boolean;
}

export interface AddOrUpdateJobTypeResult {
    success: boolean;
    error: string;
    jobType: JobType;
}

export interface LinkedJob {
    jobId: number;
    jobNumber: number;
    jobTitle: string;
    isParent: boolean;
}